import {
  Button,
  Dialog,
  IconButton,
  InfoTooltip,
  Input,
  MenuItem,
  Select, Text,
  VSpacer,
} from "@/components/DesignSystem";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { QueryKeys } from "@/constants/QueryKeys";
import { ManufacturerApi } from "@/utilities/api/ManufacturerApi";
import { RetailerApi } from "@/utilities/api/RetailerApi";
import { SubwalletApi } from "@/utilities/api/SubwalletApi";
import { useSnackbar } from "@/providers/GlobalSnackbarProvider";
import {
  CircularProgress,
  Container,
  Divider,
  InputAdornment,
  Stack,
  useTheme,
} from "@mui/material";
import Cancel from "@mui/icons-material/Cancel";
import { SubwalletEndpoint } from "@api/endpoints";
import AddIcon from "@mui/icons-material/Add";
import { RedemptionSkuInput } from "@/pages/Admin/HierarchyOfRetailers/Wallet/RedemptionSkuInput";
import { RedemptionSkuListItem }
  from "@/pages/Admin/HierarchyOfRetailers/Wallet/RedemptionSkuListItem";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";

interface CreateOrEditSubwalletDialogProps {
  onClose: () => void;
  retailerId: string,
  selectedSubwalletId: string | null,
}

export interface RedemptionSku {
  externalId: string,
  id: string | null,
  name: string,
}

const haveRedemptionSkuProductsChanged = (
  existing: RedemptionSku[],
  updated: RedemptionSku[],
) => {
  if (existing.length !== updated.length) {
    return true;
  }
  const existingKeys = new Set(existing.map(r => `${r.externalId}|${r.name}`));
  return !updated.every(r => existingKeys.has(`${r.externalId}|${r.name}`));
};

export const CreateOrEditSubwalletDialog = ({
  onClose,
  retailerId,
  selectedSubwalletId,
}: CreateOrEditSubwalletDialogProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const [isExternalIdReadonly, setIsExternalIdReadonly] = useState(false);
  const [redemptionSkuToEdit, setRedemptionSkuToEdit] = useState<RedemptionSku | undefined>({
    externalId: '',
    id: null,
    name: '',
  });
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<string | null>(null);
  const [selectedRetailerId, setSelectedRetailerId] = useState<string | null>(null);
  const [subwalletName, setSubwalletName] = useState<string | null>(null);

  const defaultDescription = "Redeemable on select products. Contact your salesperson for details.";
  const [redeemableProductsDescription, setRedeemableProductsDescription] = useState<string>(
    defaultDescription,
  );
  const [redemptionSkus, setRedemptionSkus] = useState<RedemptionSku[]>([]);

  const { data: subwallet } = useQuery(
    [QueryKeys.GET_SUBWALLET, selectedSubwalletId],
    () => SubwalletApi.get(selectedSubwalletId!),
    { enabled: !!selectedSubwalletId },
  );

  useEffect(() => {
    if (subwallet) {
      setSelectedManufacturerId(subwallet.manufacturerId);
      setSelectedRetailerId(subwallet.retailerId);
      setSubwalletName(subwallet.name);
      if (subwallet.redeemableProductsDescription) {
        setRedeemableProductsDescription(subwallet.redeemableProductsDescription);
      }
      if (subwallet.skuProducts) {
        setRedemptionSkus(subwallet.skuProducts.map(skuProduct => ({
          externalId: skuProduct.externalId!,
          id: skuProduct.id,
          name: skuProduct.name,
        })));
      }
    }
  }, [subwallet]);

  const { data: manufacturers, isFetching: areManufacturersLoading } = useQuery(
    [QueryKeys.GET_MANUFACTURERS_LIST, retailerId],
    () => ManufacturerApi.list({ retailerId }),
    { enabled: !!retailerId },
  );

  const { data: retailer, isFetching: isRetailerLoading } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, retailerId],
    () => RetailerApi.get(retailerId),
    { enabled: !!retailerId },
  );

  const { data: hasPointsHistory } = useQuery(
    [QueryKeys.HAS_POINTS_HISTORY, selectedRetailerId],
    () => SubwalletApi.hasPointsHistory(selectedSubwalletId!),
    { enabled: !!selectedSubwalletId },
  );

  const isParentRecordDisabled = !!selectedSubwalletId && !!hasPointsHistory;

  const getParentValue = () => {
    if (selectedManufacturerId) {
      return `manufacturer:${selectedManufacturerId}`;
    } else if (selectedRetailerId) {
      return `retailer:${retailerId}`;
    }
    return null;
  };

  const hasChanges = !subwallet || (
    subwallet.name !== subwalletName ||
    subwallet.redeemableProductsDescription !== redeemableProductsDescription ||
    (subwallet.skuProducts && haveRedemptionSkuProductsChanged(
      subwallet.skuProducts as RedemptionSku[], redemptionSkus,
    ))
  );

  const { mutateAsync: createSubwallet } = useMutation(
    (body: SubwalletEndpoint.Create.Request) => {
      return SubwalletApi.create(body);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.LIST_SUBWALLETS);
        openSnackbar('Subwallet created successfully');
        onClose();
        resetInputs();
      },
      onError: (error: { message: string }) => {
        openSnackbar(
          error.message || 'Error creating subwallet',
        );
      },
    },
  );

  const { mutateAsync: deleteSubwallet } = useMutation(
    (subwalletId: string) => {
      return SubwalletApi.delete(subwalletId);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.LIST_SUBWALLETS);
        openSnackbar('Subwallet deleted successfully');
        onClose();
        resetInputs();
      },
      onError: (error: { message: string }) => {
        openSnackbar(
          error.message || 'Error deleting subwallet',
        );
      },
    },
  );

  const { mutateAsync: updateSubwallet } = useMutation(
    (body: SubwalletEndpoint.Update.Request) => {
      return SubwalletApi.update(selectedSubwalletId!, body);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.LIST_SUBWALLETS);
        openSnackbar('Subwallet updated successfully');
        onClose();
        resetInputs();
      },
      onError: (error: { message: string }) => {
        openSnackbar(
          error.message || 'Error updating subwallet',
        );
      },
    },
  );

  async function onSave () {
    const subwalletData = {
      manufacturerId: selectedManufacturerId,
      name: subwalletName!,
      redeemableProductsDescription,
      redemptionSkuProducts: redemptionSkus,
      retailerId,
    };
    if (!subwalletName) {
      openSnackbar('Subwallet name is required');
      return;
    }
    if (!redeemableProductsDescription) {
      openSnackbar('Redeemable products description is required');
      return;
    }
    if (selectedSubwalletId) {
      await updateSubwallet(subwalletData);
    } else {
      await createSubwallet(subwalletData);
    }
  }

  function onAddRedemptionSku (externalId: string, id: string | null, name: string) {
    setRedemptionSkus(current => {
      const newItem = { externalId, id, name };
      const exists = current.some(item => item.externalId === externalId);
      if (exists) {
        return current.map(item =>
          item.externalId === externalId ? newItem : item,
        );
      }
      return [...current, newItem];
    });
    setRedemptionSkuToEdit(undefined);
  }

  function resetInputs () {
    setSubwalletName(null);
    setRedeemableProductsDescription(defaultDescription);
    setRedemptionSkus([]);
  }

  const SubwalletTitle = () => {
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
    const [isDeletingSubwallet, setIsDeletingSubwallet] = useState(false);

    const isCreating = !selectedSubwalletId;
    const titleText = isCreating ? 'Create subwallet' : 'Edit subwallet';

    const isRewardsProgramSubwallet = retailer?.programSubwallet?.id === selectedSubwalletId;
    const hasPromotions = !!subwallet?.promotions && subwallet.promotions.length > 0;
    const canDelete = !isCreating
      && !hasPromotions
      && !hasPointsHistory
      && !isRewardsProgramSubwallet;

    return (
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="headline-small" sx={{ overflowWrap: 'break-word' }}>
          {titleText}
        </Text>
        {canDelete && (
          <Button
            onClick={() => setShowDeleteConfirmDialog(true)}
            testID="delete-subwallet-button"
            variant="text"
          >
            Delete subwallet
          </Button>
        )}
        {showDeleteConfirmDialog && !!subwallet && (
          <ConfirmDialog
            cancelText="Go back"
            confirmText="Yes, delete"
            isLoading={isDeletingSubwallet}
            onCancel={() => setShowDeleteConfirmDialog(false)}
            onConfirm={async () => {
              setIsDeletingSubwallet(true);
              await deleteSubwallet(subwallet.id);
              setIsDeletingSubwallet(false);
              setShowDeleteConfirmDialog(false);
            }}
            open={showDeleteConfirmDialog}
            showCloseButton={false}
            sx={{ width: '385px' }}
            testID="delete-subwallet-confirm-dialog"
            title={`Delete ${subwallet.name} subwallet?`}
          >
            <Text category="body-medium">
              This action cannot be undone.
            </Text>
          </ConfirmDialog>
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={!hasChanges}
          onClick={async () => {
            await onSave();
          }}
          testID="create-subwallet-modal-save-button"
        >
          Save
        </Button>
      )}
      cancelButton={(props) => (
        <Button
          {...props}
          onClick={() => {
            onClose();
            resetInputs();
          }}
          testID="create-subwallet-modal-cancel-button"
        >
          Cancel
        </Button>
      )}
      onClose={onClose}
      open
      showCloseButton={false}
      testID="create-subwallet-modal"
      title={<SubwalletTitle />}
    >
      <VSpacer size="5" />
      {(areManufacturersLoading || isRetailerLoading) ? (
        <Container>
          <Stack alignItems="center">
            <VSpacer size="14" />
            <CircularProgress />
          </Stack>
        </Container>
      ) : (
        <Select
          disabled={isParentRecordDisabled}
          label="Parent record"
          onChangeValue={value => {
            const [type, id] = value.split(':');
            if (type === 'retailer' && retailer) {
              setSelectedRetailerId(retailer.id);
              setSelectedManufacturerId(null);
              setSubwalletName(retailer.name);
            } else {
              const manufacturer = manufacturers?.find(m => m.id === id);
              if (manufacturer) {
                setSelectedManufacturerId(id);
                setSubwalletName(manufacturer.name);
              }
            }
          }}
          required={!isParentRecordDisabled}
          testID="create-subwallet-parent-selector"
          value={getParentValue()}
          variant='outlined'
        >
          {!!retailer && (
            <MenuItem
              key={retailer.id}
              testID={retailer.id}
              value={`retailer:${retailer.id}`}
            >
              {retailer.name}
            </MenuItem>
          )}
          {manufacturers?.map((manufacturer) => (
            <MenuItem
              key={manufacturer.id}
              testID={manufacturer.id}
              value={`manufacturer:${manufacturer.id}`}
            >
              {manufacturer.name}
            </MenuItem>
          ))}
        </Select>
      )}
      {
        (!!selectedRetailerId || !!selectedManufacturerId) && (
          <>
            <VSpacer size="7" />
            <Input
              InputProps={{
                endAdornment: !!subwalletName && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setSubwalletName('')}
                      testID="subwallet-name-clear-button"
                    >
                      <Cancel />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              label="Subwallet name"
              maxLength={100}
              onChange={(e) => setSubwalletName(e.target.value)}
              required
              testID="subwallet-name"
              value={subwalletName}
              variant="outlined"
            />
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="end"
              sx={{ color: theme.palette.primary.main }}>
              <InfoTooltip placement="top" variant="help">
                The product description is used to describe the products that
                the earned points within this subwallet can be redeemed on.
              </InfoTooltip>
            </Stack>
            <VSpacer size="2" />
            <Input
              label="Redeemable products description"
              maxLength={500}
              minRows={2}
              multiline
              onChangeText={(value) => setRedeemableProductsDescription(value)}
              required
              showCharacterLimit='message'
              testID="redeemable-products-description-input"
              value={redeemableProductsDescription}
              variant="outlined"
            />

            <VSpacer size="6" />
            <Text category="body-xlarge">
              Add redemption SKUs
            </Text>
            <VSpacer size="5" />
            {redemptionSkus.map(redemptionSku => (
              <Stack justifyItems="center" key={redemptionSku.externalId}>
                <Divider />
                <VSpacer size="3" />
                <RedemptionSkuListItem
                  onDelete={() => {
                    setRedemptionSkus(current => (
                      current.filter(c => c.externalId !== redemptionSku.externalId)
                    ));
                    if (redemptionSkus.length === 1) {
                      setRedemptionSkuToEdit({ externalId: '', id: null, name: '' });
                    } else {
                      setRedemptionSkuToEdit(undefined);
                    }
                  }}
                  onEdit={(hasPointsHistory: boolean) => {
                    if (redemptionSkuToEdit) {
                      if (redemptionSkuToEdit.name && redemptionSkuToEdit.externalId) {
                        onAddRedemptionSku(
                          redemptionSkuToEdit.externalId,
                          redemptionSkuToEdit.id,
                          redemptionSkuToEdit.name,
                        );
                      }
                    }
                    setIsExternalIdReadonly(hasPointsHistory);
                    setRedemptionSkuToEdit(redemptionSku);
                    setRedemptionSkus(current => (
                      current.filter(c => c.externalId !== redemptionSku.externalId)
                    ));
                  }}
                  redemptionSku={redemptionSku}
                  subwalletId={subwallet?.id}
                />
                <VSpacer size="3" />
              </Stack>
            ))}
            {!!redemptionSkus.length && (
              <>
                <Divider />
                <VSpacer size="5" />
              </>
            )}
            {!!redemptionSkuToEdit && (
              <RedemptionSkuInput
                existingRedemptionSku={redemptionSkuToEdit}
                isExternalIdReadonly={isExternalIdReadonly}
                onCancel={() => {
                  if (redemptionSkuToEdit.name && redemptionSkuToEdit.externalId) {
                    onAddRedemptionSku(
                      redemptionSkuToEdit.externalId,
                      redemptionSkuToEdit.id,
                      redemptionSkuToEdit.name,
                    );
                  }
                  if (!redemptionSkus.length) {
                    setRedemptionSkuToEdit({ externalId: '', id: null, name: '' });
                  } else {
                    setRedemptionSkuToEdit(undefined);
                  }
                }}
                onSave={onAddRedemptionSku}
                redemptionSkus={redemptionSkus}
              />
            )}
            {!redemptionSkuToEdit && (
              <Button
                onClick={() => {
                  setRedemptionSkuToEdit({
                    externalId: '',
                    id: null,
                    name: '',
                  });
                }}
                square
                startIcon={<AddIcon />}
                sx={{ height: '32px' }}
                testID="show-redemption-sku-input"
                >
                Add SKU
              </Button>
            )}
          </>
        )
      }
    </Dialog>
  );
};